import { Vector2 } from "../../common/vector2.js";
import { Rect } from "../../common/rect.js";
import { UIElement } from "./ui_element.js";
import { Game } from "../../game.js";

export interface IScrollableContainerCSSOptions
{
  scroll_speed?: number;
  momentum_friction?: number;
  enable_momentum?: boolean;
  enable_touch_scroll?: boolean;
  scroll_sensitivity?: number;
  enable_rubber_band?: boolean;
  rubber_band_resistance?: number;
  rubber_band_max_distance?: number;
  rubber_band_progression?: number;
  clip_horizontal_overflow?: number;
  /** Before the first vertical scroll gesture, shrink the clip height by this many CSS pixels; full height after. */
  pre_scroll_viewport_shrink?: number;
  /**
   * If true (and shrink > 0), do not expand the viewport on the first vertical drag; call
   * `expandPreScrollViewportIfNeeded()` from outside (e.g. when a floating row starts tracking scroll).
   */
  pre_scroll_defer_expand_until_external?: boolean;
  /** Cap the DPR used for the content canvas (default: device DPR). Useful to limit memory on high-DPR screens. */
  content_dpr_cap?: number;
  /**
   * No content canvases at all: the owner puts DOM rows into `getContentLayer()` and recycles them,
   * the layer only moves with the scroll. Canvas tiles cost width x content height x 4 bytes and stay
   * allocated while the screen is pooled; a layer of DOM rows is rasterized by the browser only where
   * it is visible, and its tiles are dropped once the container is hidden.
   */
  virtual_content?: boolean;
  /**
   * Virtual mode only: the browser scrolls the list itself (`overflow-y: scroll`). Touch scrolling and
   * the fling then run on the compositor thread, so a busy main thread (a row being rebound, GC, the
   * game loop) no longer stalls the list: it only delays rows appearing at the edge of the overscan.
   * The scroller takes its own input (`setupNativeDOM`); programmatic offsets write `scrollTop`.
   */
  native_scroll?: boolean;
  /**
   * Native mode: the scroller box reaches this far above / below the container (rows keep their
   * place), for sticky content that sticks out of the list (`getStickyLayer`). Rows scrolled into
   * these bands stay visible unless the owner covers them.
   */
  native_extend_top?: number;
  native_extend_bottom?: number;
}

const NATIVE_SCROLL_CLASS = "ui-native-scroll";
let native_scroll_styles_injected = false;

function ensureNativeScrollStyles(): void
{
  if ( native_scroll_styles_injected )
    return;
  native_scroll_styles_injected = true;
  const style = document.createElement( "style" );
  style.id = "ui-native-scroll-style";
  style.textContent = `.${NATIVE_SCROLL_CLASS}{scrollbar-width:none}.${NATIVE_SCROLL_CLASS}::-webkit-scrollbar{display:none}`;
  document.head.appendChild( style );
}

interface ContentTile
{
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  top: number;
  height: number;
  transform: string;
}

export class UIScrollableContainerCSS extends UIElement
{
  private clip_div!: HTMLDivElement;
  private content_canvas!: HTMLCanvasElement;
  private content_ctx!: CanvasRenderingContext2D;
  private static readonly MAX_TILE_DEVICE_PX = 4096;
  private tiles: ContentTile[] = [];
  private content_layer: HTMLDivElement | null = null;
  private content_layer_transform = "";
  private row_pitch: number = 0;
  private row_offset: number = 0;

  private viewport_rect!: Rect;
  private content_height: number = 0;
  private scroll_offset: number = 0;
  private max_scroll_offset: number = 0;
  private is_over_scrolling: boolean = false;

  private options: Required<IScrollableContainerCSSOptions>;
  private rubber_band_exp_factor: number = 0;

  private scroll_velocity: number = 0;
  private touch_start_pos: Vector2 | null = null;
  private touch_last_pos: Vector2 | null = null;
  private touch_last_time: number = 0;
  private is_scrolling: boolean = false;
  private swipe_direction: "up" | "down" | null = null;

  private snap_targets: number[] = [];
  private scroll_end_callback?: () => void;
  private current_snap_section: number = -1;
  private snap_section_change_callback?: ( new_section: number ) => void;
  private last_update_time: number | null = null;
  private is_raf_animating: boolean = false;
  private raf_id: number = 0;

  private last_transform_str: string = '';
  private log_friction: number = 0;
  private velocity_samples: number[] = [];
  private static readonly VELOCITY_WINDOW = 4;

  private clip_h_overflow: number = 0;

  private full_viewport_height: number = 0;
  private pre_scroll_shrink: number = 0;
  private viewport_collapsed: boolean = false;

  private scroll_change_callback?: () => void;
  private notified_scroll_offset: number = 0;

  // Native mode (`native_scroll`): the scroller's inner box and the layers in it.
  private native: boolean = false;
  private native_sizer: HTMLDivElement | null = null;
  private sticky_layer: HTMLDivElement | null = null;
  private ext_top: number = 0;
  private ext_bottom: number = 0;
  private native_tap_callback?: ( view_x: number, view_y: number ) => void;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: IScrollableContainerCSSOptions = {}
  )
  {
    super( ctx, x, y, width, height );

    this.options = {
      scroll_speed:              options.scroll_speed              ?? 1.0,
      momentum_friction:         options.momentum_friction         ?? 0.95,
      enable_momentum:           options.enable_momentum           ?? true,
      enable_touch_scroll:       options.enable_touch_scroll       ?? true,
      scroll_sensitivity:        options.scroll_sensitivity        ?? 1.0,
      enable_rubber_band:        options.enable_rubber_band        ?? true,
      rubber_band_resistance:    options.rubber_band_resistance    ?? 0.3,
      rubber_band_max_distance:  options.rubber_band_max_distance  ?? 100,
      rubber_band_progression:   options.rubber_band_progression   ?? 3.5,
      clip_horizontal_overflow:  options.clip_horizontal_overflow  ?? 0,
      pre_scroll_viewport_shrink: options.pre_scroll_viewport_shrink ?? 0,
      pre_scroll_defer_expand_until_external: options.pre_scroll_defer_expand_until_external ?? false,
      content_dpr_cap:           options.content_dpr_cap           ?? Infinity,
      virtual_content:           options.virtual_content           ?? false,
      native_scroll:             options.native_scroll             ?? false,
      native_extend_top:         options.native_extend_top         ?? 0,
      native_extend_bottom:      options.native_extend_bottom      ?? 0,
    };

    this.rubber_band_exp_factor = this.options.rubber_band_progression * Math.LN2;
    this.log_friction            = Math.log( this.options.momentum_friction );
    this.clip_h_overflow = this.options.clip_horizontal_overflow;
    this.native = this.options.native_scroll && this.options.virtual_content;
    if ( this.native )
    {
      this.ext_top = Math.max( 0, this.options.native_extend_top );
      this.ext_bottom = Math.max( 0, this.options.native_extend_bottom );
      // The owner covers the bottom of the list itself (sticky content); the viewport is always full.
      this.options.pre_scroll_viewport_shrink = 0;
    }

    this.full_viewport_height = height;
    this.pre_scroll_shrink    = this.options.pre_scroll_viewport_shrink;
    this.viewport_collapsed   = this.pre_scroll_shrink > 0;
    const initial_viewport_h = this.viewport_collapsed
      ? Math.max( 1, height - this.pre_scroll_shrink )
      : height;
    this.viewport_rect = new Rect( 0, 0, width, initial_viewport_h );

    this.setupDOM( width, initial_viewport_h );
  }

  private setupDOM( width: number, height: number ): void
  {
    if ( this.native )
    {
      this.setupNativeDOM( width, height );
      return;
    }

    this.clip_div = document.createElement( "div" );
    this.clip_div.style.cssText = [
      "position:absolute",
      "left:0px",
      "top:0px",
      `width:${width + this.clip_h_overflow * 2}px`,
      `height:${height}px`,
      "overflow:hidden",
      "pointer-events:none",
      "z-index:10",
    ].join( ";" );

    if ( this.options.virtual_content )
    {
      const layer = document.createElement( "div" );
      layer.className = "ui-scroll-layer";
      layer.style.cssText = [
        "position:absolute",
        `left:${this.clip_h_overflow}px`,
        "top:0",
        `width:${width}px`,
        "height:0",
        "will-change:transform",
        "pointer-events:none",
      ].join( ";" );
      this.clip_div.appendChild( layer );
      this.content_layer = layer;
      document.body.appendChild( this.clip_div );
      return;
    }

    const tile = this.createTile( width, 0, 0 );
    this.tiles = [ tile ];
    this.content_canvas = tile.canvas;
    document.body.appendChild( this.clip_div );

    this.content_ctx = this.tiles[0].ctx;
    this.applyContentCtxBaseTransform();
  }

  /**
   * Native mode: the clip is the scroller. Inside it a sizer gives the scroll height (rows plus the
   * extension bands), the row layer starts below the top band, and the sticky layer spans the whole
   * scroll height so sticky content in it can stick anywhere in the list.
   */
  private setupNativeDOM( width: number, height: number ): void
  {
    ensureNativeScrollStyles();
    const clip = document.createElement( "div" );
    clip.className = NATIVE_SCROLL_CLASS;
    clip.style.cssText = [
      "position:absolute",
      "left:0px",
      "top:0px",
      `width:${width + this.clip_h_overflow * 2}px`,
      `height:${height + this.ext_top + this.ext_bottom}px`,
      "overflow-x:hidden",
      "overflow-y:scroll",
      "overscroll-behavior:contain",
      // Rows are recycled under the viewport: scroll anchoring must never chase a moved row.
      "overflow-anchor:none",
      // The page is touch-action:none; a scroller enables panning again for its own subtree.
      "touch-action:pan-y",
      "-webkit-overflow-scrolling:touch",
      "pointer-events:auto",
      "z-index:10",
    ].join( ";" );

    const sizer = document.createElement( "div" );
    sizer.style.cssText = [
      "position:relative",
      "width:100%",
      `height:${this.ext_top + this.ext_bottom}px`,
      "pointer-events:none",
    ].join( ";" );

    const layer = document.createElement( "div" );
    layer.className = "ui-scroll-layer";
    layer.style.cssText = [
      "position:absolute",
      `left:${this.clip_h_overflow}px`,
      `top:${this.ext_top}px`,
      `width:${width}px`,
      "height:0",
      "pointer-events:none",
    ].join( ";" );

    const sticky = document.createElement( "div" );
    sticky.className = "ui-scroll-sticky";
    sticky.style.cssText = [
      "position:absolute",
      "left:0",
      "top:0",
      "width:100%",
      "height:100%",
      "pointer-events:none",
    ].join( ";" );

    sizer.appendChild( layer );
    sizer.appendChild( sticky );
    clip.appendChild( sizer );
    document.body.appendChild( clip );

    clip.addEventListener( "scroll", () => this.onNativeScroll(), { passive: true } );
    // The list sits above the screen canvas and takes its own input: taps of every pointer type
    // come as clicks, mouse drags are turned into scrollTop here (a touch pans natively).
    clip.addEventListener( "click", ( e ) => this.onNativeClick( e ) );
    clip.addEventListener( "pointerdown", ( e ) => this.onNativePointerDown( e ) );
    clip.addEventListener( "pointermove", ( e ) => this.onNativePointerMove( e ) );
    clip.addEventListener( "pointerup", ( e ) => this.onNativePointerUp( e ) );
    clip.addEventListener( "pointercancel", ( e ) => this.onNativePointerUp( e ) );

    this.clip_div = clip;
    this.native_sizer = sizer;
    this.content_layer = layer;
    this.sticky_layer = sticky;
  }

  private onNativeScroll(): void
  {
    const top = Math.max( 0, Math.min( this.clip_div.scrollTop, this.max_scroll_offset ) );
    if ( top === this.scroll_offset )
      return;
    this.scroll_offset = top;
    this.checkSnapSectionChange();
    this.notifyScrollChange();
  }

  private onNativeClick( e: MouseEvent ): void
  {
    // The click that ends a mouse drag is not a tap.
    if ( this.mouse_dragged || !this.native_tap_callback )
    {
      this.mouse_dragged = false;
      return;
    }
    const box = this.clip_div.getBoundingClientRect();
    this.native_tap_callback( e.clientX - box.left - this.clip_h_overflow, e.clientY - box.top - this.ext_top );
  }

  private mouse_drag: { id: number; y: number; top: number } | null = null;
  private mouse_dragged = false;

  private onNativePointerDown( e: PointerEvent ): void
  {
    this.mouse_dragged = false;
    if ( e.pointerType !== "mouse" || e.button !== 0 )
      return;
    this.mouse_drag = { id: e.pointerId, y: e.clientY, top: this.clip_div.scrollTop };
  }

  private onNativePointerMove( e: PointerEvent ): void
  {
    const drag = this.mouse_drag;
    if ( !drag || e.pointerId !== drag.id )
      return;
    const dy = e.clientY - drag.y;
    if ( !this.mouse_dragged && Math.abs( dy ) > 6 )
    {
      this.mouse_dragged = true;
      this.clip_div.setPointerCapture( e.pointerId );
    }
    if ( this.mouse_dragged )
      this.clip_div.scrollTop = drag.top - dy;
  }

  private onNativePointerUp( e: PointerEvent ): void
  {
    if ( !this.mouse_drag || e.pointerId !== this.mouse_drag.id )
      return;
    this.mouse_drag = null;
    if ( this.clip_div.hasPointerCapture( e.pointerId ) )
      this.clip_div.releasePointerCapture( e.pointerId );
  }

  /**
   * Native mode: a tap or click on the list, in the container's own coordinates (y = 0 at its
   * top, whatever the scroll offset). The game's own touch events no longer reach the list.
   */
  public setOnNativeTap( callback: ( view_x: number, view_y: number ) => void ): void
  {
    this.native_tap_callback = callback;
  }

  public isNativeScroll(): boolean
  {
    return this.native;
  }

  /** Native mode: spans the whole scroll height above the rows, at scroller coordinates (top band included). */
  public getStickyLayer(): HTMLDivElement
  {
    if ( !this.sticky_layer )
      throw new Error( "[UIScrollableContainerCSS] getStickyLayer() needs native_scroll" );
    return this.sticky_layer;
  }

  /** Virtual mode only: the moving layer the owner puts its rows into, at their content y. */
  public getContentLayer(): HTMLDivElement
  {
    if ( !this.content_layer )
      throw new Error( "[UIScrollableContainerCSS] getContentLayer() needs virtual_content" );
    return this.content_layer;
  }

  private createTile( width: number, top: number, height: number ): ContentTile
  {
    const dpr = this.getContentDpr();
    const canvas = document.createElement( "canvas" );
    canvas.width  = Math.ceil( ( width + this.clip_h_overflow * 2 ) * dpr );
    canvas.height = Math.max( 1, Math.ceil( height * dpr ) );
    canvas.style.cssText = [
      "position:absolute",
      "left:0",
      "top:0",
      `width:${width + this.clip_h_overflow * 2}px`,
      `height:${height}px`,
      "will-change:transform",
      "pointer-events:none",
    ].join( ";" );
    this.clip_div.appendChild( canvas );
    const ctx = canvas.getContext( "2d" )!;
    return { canvas, ctx, top, height, transform: "" };
  }

  private getContentDpr(): number
  {
    return Math.min( Game.getDpr(), this.options.content_dpr_cap );
  }

  private applyContentCtxBaseTransform(): void
  {
    const dpr = this.getContentDpr();
    for ( const tile of this.tiles )
      tile.ctx.setTransform( dpr, 0, 0, dpr, this.clip_h_overflow * dpr, -tile.top * dpr );
  }

  public getContentCtxAt( y: number ): CanvasRenderingContext2D
  {
    for ( let i = this.tiles.length - 1; i >= 0; i-- )
    {
      if ( y >= this.tiles[i].top )
        return this.tiles[i].ctx;
    }
    return this.tiles[0].ctx;
  }

  public getContentCanvases(): HTMLCanvasElement[]
  {
    return this.tiles.map( tile => tile.canvas );
  }

  private tileBoundaries( height: number ): number[]
  {
    const dpr = this.getContentDpr();
    const tail = this.row_pitch;
    const max_visible = Math.max( 1, UIScrollableContainerCSS.MAX_TILE_DEVICE_PX / dpr - tail );
    const ends: number[] = [];
    let cur = 0;
    while ( cur < height )
    {
      let end = Math.min( height, cur + max_visible );
      if ( this.row_pitch > 0 && end < height )
      {
        const k = Math.floor( ( end - this.row_offset ) / this.row_pitch );
        const snapped = this.row_offset + k * this.row_pitch;
        if ( snapped > cur )
          end = snapped;
      }
      ends.push( end );
      cur = end;
    }
    if ( ends.length === 0 )
      ends.push( height );
    return ends;
  }

  private layoutTiles(): void
  {
    const dpr = this.getContentDpr();
    const w = this.viewport_rect.getW() + this.clip_h_overflow * 2;
    const ends = this.tileBoundaries( this.content_height );
    while ( this.tiles.length > ends.length )
    {
      const tile = this.tiles.pop() as ContentTile;
      tile.canvas.remove();
    }
    let top = 0;
    for ( let i = 0; i < ends.length; i++ )
    {
      const is_last = i === ends.length - 1;
      const height = ends[i] - top + ( is_last ? 0 : this.row_pitch );
      if ( i >= this.tiles.length )
        this.tiles.push( this.createTile( this.viewport_rect.getW(), top, height ) );
      const tile = this.tiles[i];
      tile.top = top;
      tile.height = height;
      tile.canvas.width = Math.ceil( w * dpr );
      tile.canvas.height = Math.max( 1, Math.ceil( height * dpr ) );
      tile.canvas.style.height = `${height}px`;
      tile.transform = "";
      top = ends[i];
    }
    this.content_canvas = this.tiles[0].canvas;
    this.content_ctx = this.tiles[0].ctx;
    this.applyContentCtxBaseTransform();
  }

  public updateDOMPosition(): void
  {
    const abs  = this.getAbsolutePosition();
    const h_ov = this.clip_h_overflow;
    this.clip_div.style.left = `${Game.getGameX() + abs.x - h_ov}px`;
    this.clip_div.style.top  = `${abs.y - this.ext_top}px`;
  }


  public getContentCtx(): CanvasRenderingContext2D
  {
    return this.content_ctx;
  }

  public getOverlayHost(): HTMLDivElement
  {
    return this.clip_div;
  }

  /** How far the clip reaches past the container on each side (`clip_horizontal_overflow`). */
  public getClipOverflowX(): number
  {
    return this.clip_h_overflow;
  }

  public setContentHeight( height: number, row_pitch: number = 0, row_offset: number = 0 ): void
  {
    this.content_height = height;
    this.row_pitch = Math.max( 0, row_pitch );
    this.row_offset = row_offset;
    if ( this.native_sizer )
      this.native_sizer.style.height = `${this.ext_top + Math.max( 0, height ) + this.ext_bottom}px`;
    else if ( !this.content_layer )
      this.layoutTiles();
    this.recomputeScrollLimits();
  }

  private recomputeScrollLimits(): void
  {
    this.max_scroll_offset = this.content_height > 0
      ? Math.max( 0, this.content_height - this.viewport_rect.getH() )
      : 0;
    // A rubber-band pull past the top keeps its distance (the drag's end snaps it back):
    // clamping it here snapped the list back in one frame while the finger was still pulling,
    // and the next move pulled it down again. The leaderboard collapses its viewport as soon
    // as the player's card reaches the bottom, which a pull at the top does when the player
    // is near the top of the board.
    const pulled_past_top = this.options.enable_rubber_band && this.scroll_offset < 0;
    this.scroll_offset = pulled_past_top
      ? this.scroll_offset
      : Math.max( 0, Math.min( this.scroll_offset, this.max_scroll_offset ) );
    if ( this.snap_targets.length > 0 )
      this.snap_targets = this.snap_targets.map( ( t ) => Math.max( 0, Math.min( t, this.max_scroll_offset ) ) );
    this.applyScrollTransform();
    this.checkSnapSectionChange();
  }

  private expandViewportIfCollapsed(): void
  {
    if ( !this.viewport_collapsed ) return;
    this.viewport_collapsed = false;
    this.viewport_rect = new Rect( 0, 0, this.viewport_rect.getW(), this.full_viewport_height );
    this.clip_div.style.height = `${this.full_viewport_height}px`;
    this.recomputeScrollLimits();
  }

  /**
   * Expands a collapsed pre-scroll viewport once; no-op if already full size or shrink is 0.
   * Skipped while the current offset would not fit the taller viewport: expanding would clamp
   * the offset, which makes the caller collapse again on the next frame and the list jumps.
   */
  public expandPreScrollViewportIfNeeded(): void
  {
    if ( !this.viewport_collapsed ) return;
    const expanded_max = Math.max( 0, this.content_height - this.full_viewport_height );
    if ( this.content_height > 0 && this.scroll_offset > expanded_max ) return;
    this.expandViewportIfCollapsed();
  }

  /** Shrinks back by `pre_scroll_viewport_shrink` after expand; no-op if shrink is 0 or already collapsed. */
  public collapsePreScrollViewportIfNeeded(): void
  {
    if ( this.pre_scroll_shrink <= 0 || this.viewport_collapsed ) return;
    this.viewport_collapsed = true;
    const vh = Math.max( 1, this.full_viewport_height - this.pre_scroll_shrink );
    this.viewport_rect = new Rect( 0, 0, this.viewport_rect.getW(), vh );
    this.clip_div.style.height = `${vh}px`;
    this.recomputeScrollLimits();
  }

  public clearContent(): void
  {
    for ( const tile of this.tiles )
    {
      tile.ctx.save();
      tile.ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      tile.ctx.clearRect( 0, 0, tile.canvas.width, tile.canvas.height );
      tile.ctx.restore();
    }
  }

  public clearScrollableChildren(): void
  {
    cancelAnimationFrame( this.raf_id );
    this.scroll_velocity   = 0;
    this.scroll_offset     = 0;
    this.max_scroll_offset = 0;
    this.content_height    = 0;
    this.is_raf_animating  = false;
    this.velocity_samples  = [];
    this.last_transform_str = '';
    this.collapsePreScrollViewportIfNeeded();
    this.clearContent();
    this.applyScrollTransform();
  }

  public getScrollOffset(): number
  {
    return this.scroll_offset;
  }

  /** Puts the list back to an offset after a content rebuild, without expanding the viewport. */
  public restoreScrollOffset( offset: number ): void
  {
    this.scroll_offset = Math.max( 0, Math.min( offset, this.max_scroll_offset ) );
    this.applyScrollTransform();
    this.checkSnapSectionChange();
  }

  /**
   * Called synchronously whenever the offset changes (touch move, momentum, snap animation),
   * so DOM overlays that follow a row move in the same frame as the content canvases.
   */
  public setOnScrollChange( callback: () => void ): void
  {
    this.scroll_change_callback = callback;
  }

  public getMaxScrollOffset(): number
  {
    return this.max_scroll_offset;
  }

  public setOnScrollEnd( callback: () => void ): void
  {
    this.scroll_end_callback = callback;
  }

  public setSnapTargets( targets: number[] ): void
  {
    this.snap_targets = targets.map( ( t ) => Math.max( 0, Math.min( t, this.max_scroll_offset ) ) );
  }

  public setOnSnapSectionChange( callback: ( new_section: number ) => void ): void
  {
    this.snap_section_change_callback = callback;
  }

  public scrollTo( offset: number, animate: boolean = true ): void
  {
    this.expandViewportIfCollapsed();
    const clamped = Math.max( 0, Math.min( offset, this.max_scroll_offset ) );
    if ( animate )
    {
      this.animateScrollTo( clamped );
    }
    else
    {
      this.scroll_offset = clamped;
      this.applyScrollTransform();
      this.checkSnapSectionChange();
      if ( this.scroll_end_callback ) this.scroll_end_callback();
    }
  }

  /**
   * Gets to a far offset without scrolling through everything in between: jumps to one viewport
   * short of it at once, then glides the last viewport. A virtual list rebinds rows for about two
   * screens instead of every row on the way (and loads no avatars for rows nobody saw).
   */
  public glideTo( offset: number, glide_px: number = this.viewport_rect.getH() ): void
  {
    this.expandViewportIfCollapsed();
    this.scroll_velocity = 0;
    const target = Math.max( 0, Math.min( offset, this.max_scroll_offset ) );
    const distance = target - this.scroll_offset;
    if ( Math.abs( distance ) > glide_px )
    {
      this.scroll_offset = target - Math.sign( distance ) * glide_px;
      this.applyScrollTransform();
    }
    if ( this.native )
    {
      // The browser's smooth scroll runs on the compositor; offsets come back as scroll events.
      cancelAnimationFrame( this.raf_id );
      this.is_raf_animating = false;
      this.clip_div.scrollTo( { top: target, behavior: "smooth" } );
      return;
    }
    this.animateScrollTo( target );
  }

  public getCurrentSnapSection(): number
  {
    const current = this.scroll_offset;
    if ( this.snap_targets.length === 0 ) return 0;
    if ( current < this.snap_targets[0] ) return 0;
    for ( let i = 0; i < this.snap_targets.length - 1; i++ )
    {
      if ( current >= this.snap_targets[i] && current < this.snap_targets[i + 1] )
        return i;
    }
    return this.snap_targets.length - 1;
  }

  public hideElement( duration: number = 300 ): void
  {
    // DOM-only fade: a canvas-side opacity transform would dirty the parent
    // tree every frame while this element draws nothing on the main canvas.
    this.clip_div.style.transition = duration > 16 ? `opacity ${duration}ms ease-out` : "";
    this.clip_div.style.opacity = "0";
    if ( duration <= 16 )
      this.clip_div.style.display = "none";
  }

  public showElement( duration: number = 300 ): void
  {
    this.clip_div.style.display = "";
    // A scroller loses its offset while it is display:none.
    if ( this.native && this.clip_div.scrollTop !== this.scroll_offset )
      this.clip_div.scrollTop = this.scroll_offset;
    if ( duration > 16 )
    {
      this.clip_div.style.transition = "";
      this.clip_div.style.opacity = "0";
      requestAnimationFrame( () =>
      {
        this.clip_div.style.transition = `opacity ${duration}ms ease-in`;
        this.clip_div.style.opacity = "1";
      } );
    }
    else
    {
      this.clip_div.style.transition = "";
      this.clip_div.style.opacity = "1";
    }
  }

  public destroy(): void
  {
    cancelAnimationFrame( this.raf_id );
    if ( this.clip_div.parentNode )
    {
      this.clip_div.parentNode.removeChild( this.clip_div );
    }
  }


  private applyScrollTransform(): void
  {
    for ( const tile of this.tiles )
    {
      const val = `translate3d(0,${tile.top - this.scroll_offset}px,0)`;
      if ( val === tile.transform ) continue;
      tile.transform = val;
      tile.canvas.style.transform = val;
    }
    if ( this.native )
    {
      // Programmatic offsets (jump, restore, mouse drag); the browser's own scrolling is read back
      // in `onNativeScroll`. A fractional scrollTop can differ from the offset by under a pixel.
      const top = Math.max( 0, this.scroll_offset );
      if ( Math.abs( this.clip_div.scrollTop - top ) >= 0.5 )
        this.clip_div.scrollTop = top;
    }
    else if ( this.content_layer )
    {
      const val = `translate3d(0,${-this.scroll_offset}px,0)`;
      if ( val !== this.content_layer_transform )
      {
        this.content_layer_transform = val;
        this.content_layer.style.transform = val;
      }
    }
    this.notifyScrollChange();
  }

  private notifyScrollChange(): void
  {
    if ( this.scroll_offset !== this.notified_scroll_offset )
    {
      this.notified_scroll_offset = this.scroll_offset;
      this.scroll_change_callback?.();
    }
  }

  private updateScrollOffset( new_offset: number ): void
  {
    const clamped = Math.max( 0, Math.min( new_offset, this.max_scroll_offset ) );
    if ( clamped !== this.scroll_offset )
    {
      this.scroll_offset = clamped;
      this.applyScrollTransform();
      this.checkSnapSectionChange();
    }
  }

  private updateScrollOffsetRubberBand( new_offset: number ): void
  {
    this.is_over_scrolling = new_offset < 0 || new_offset > this.max_scroll_offset;
    if ( new_offset !== this.scroll_offset )
    {
      this.scroll_offset = new_offset;
      this.applyScrollTransform();
      this.checkSnapSectionChange();
    }
  }

  private checkSnapSectionChange(): void
  {
    const new_section = this.getCurrentSnapSection();
    if ( new_section !== this.current_snap_section )
    {
      this.current_snap_section = new_section;
      if ( this.snap_section_change_callback )
        this.snap_section_change_callback( new_section );
    }
  }

  private applyRubberBandEffect( delta: number ): number
  {
    if ( !this.options.enable_rubber_band ) return delta;

    const new_offset = this.scroll_offset + delta;
    let distance_beyond: number;

    if ( new_offset < 0 )
      distance_beyond = -new_offset;
    else if ( new_offset > this.max_scroll_offset )
      distance_beyond = new_offset - this.max_scroll_offset;
    else
      return delta;

    const max_distance = this.options.rubber_band_max_distance;
    if ( distance_beyond > max_distance ) return delta * 0.05;

    const progress = distance_beyond / max_distance;
    const geometric_factor = Math.exp( progress * this.rubber_band_exp_factor );
    const resistance = 1 - ( 1 / geometric_factor );

    return delta * ( 1 - resistance * this.options.rubber_band_resistance );
  }

  private snapBackToBounds(): void
  {
    if ( !this.options.enable_rubber_band ) return;

    let target = this.scroll_offset;
    if ( this.scroll_offset < 0 ) target = 0;
    else if ( this.scroll_offset > this.max_scroll_offset ) target = this.max_scroll_offset;

    if ( target !== this.scroll_offset )
    {
      this.is_over_scrolling = false;
      this.animateScrollTo( target );
    }
  }

  private snapToNearestTarget( direction: "up" | "down" | null ): void
  {
    if ( this.snap_targets.length === 0 ) return;

    const current = this.scroll_offset;

    for ( let i = 0; i < this.snap_targets.length; i++ )
    {
      const start = this.snap_targets[i];
      const end   = this.snap_targets[i + 1] ?? this.max_scroll_offset;
      const range = end - start;

      if ( current >= start && current < end )
      {
        let threshold: number;
        if ( direction === "down" )      threshold = start + range * ( 2 / 3 );
        else if ( direction === "up" )   threshold = start + range * ( 1 / 3 );
        else                             threshold = start + range * 0.5;

        this.scrollTo( current < threshold ? start : end, true );
        return;
      }
    }

    const closest = this.snap_targets.reduce( ( a, b ) =>
      Math.abs( b - current ) < Math.abs( a - current ) ? b : a
    );
    this.scrollTo( closest, true );
  }

  private animateScrollTo( target: number ): void
  {
    cancelAnimationFrame( this.raf_id );

    if ( Math.abs( target - this.scroll_offset ) < 1 )
    {
      this.scroll_offset    = target;
      this.is_raf_animating = false;
      this.applyScrollTransform();
      this.checkSnapSectionChange();
      if ( this.scroll_end_callback ) this.scroll_end_callback();
      return;
    }

    const start    = this.scroll_offset;
    const delta    = target - start;
    const duration = 300;
    const t0       = performance.now();
    this.is_raf_animating = true;

    const tick = ( now: number ) =>
    {
      const t    = Math.min( 1, ( now - t0 ) / duration );
      const ease = t * ( 2 - t );
      this.scroll_offset = start + delta * ease;
      this.applyScrollTransform();

      if ( t < 1 )
      {
        this.raf_id = requestAnimationFrame( tick );
      }
      else
      {
        this.scroll_offset    = target;
        this.is_raf_animating = false;
        this.applyScrollTransform();
        this.checkSnapSectionChange();
        if ( this.scroll_end_callback ) this.scroll_end_callback();
      }
    };

    this.raf_id = requestAnimationFrame( tick );
  }


  private isInViewport( p: Vector2 ): boolean
  {
    const local_p = this.toLocalCoordinates( p );
    return this.viewport_rect.contains( local_p.x, local_p.y );
  }

  private toLocalCoordinates( p: Vector2 ): Vector2
  {
    const pixel_pos = this.pixelPosForRender();
    return new Vector2( p.x - pixel_pos.x, p.y - pixel_pos.y );
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    // Native mode takes its input from its own DOM node (`setupNativeDOM`). The game's events can
    // still bring a touch here (pointer events on window on a non-mobile device); scrolling it
    // from JS as well would fight the browser's own pan.
    if ( this.native )
      return false;

    // The finger takes over a running snap-back/snap animation; left running, its frames kept
    // rewriting the offset under the drag, and a short list (that hits its ends often) jittered.
    if ( this.is_raf_animating )
    {
      cancelAnimationFrame( this.raf_id );
      this.is_raf_animating = false;
    }

    this.touch_start_pos  = p.clone();
    this.touch_last_pos   = p.clone();
    this.touch_last_time  = now;
    this.is_scrolling     = false;
    this.scroll_velocity  = 0;
    this.velocity_samples = [];
    this.last_update_time = now;

    const handled = super.onTouchStart( p, now );
    if ( handled || this.isInViewport( p ) )
      return true;

    return false;
  }

  public onTouchMove( p: Vector2, now: number ): void
  {
    if ( this.native )
      return;

    if ( this.touch_start_pos && this.options.enable_touch_scroll )
    {
      const time_delta = now - this.touch_last_time;

      if ( !this.is_scrolling )
      {
        const total_delta    = p.subtract( this.touch_start_pos );
        const scroll_threshold = 6;
        if ( Math.abs( total_delta.y ) > scroll_threshold )
        {
          this.is_scrolling = true;
          // Scroll from where the slop ends: measuring the first move from the touch start
          // made up the whole slop at once, and the list hopped ~6 px under the finger.
          this.touch_last_pos = new Vector2( p.x, this.touch_start_pos.y + Math.sign( total_delta.y ) * scroll_threshold );
          if ( !this.options.pre_scroll_defer_expand_until_external )
            this.expandViewportIfCollapsed();
        }
      }

      if ( this.is_scrolling )
      {
        const delta          = p.subtract( this.touch_last_pos! );
        const raw_delta      = -delta.y * this.options.scroll_sensitivity;
        const adjusted_delta = this.applyRubberBandEffect( raw_delta );
        const new_offset     = this.scroll_offset + adjusted_delta;

        if ( this.options.enable_rubber_band )
          this.updateScrollOffsetRubberBand( new_offset );
        else
          this.updateScrollOffset( new_offset );

        if ( time_delta > 0 )
        {
          const raw_v = -delta.y / time_delta;
          this.velocity_samples.push( raw_v );
          if ( this.velocity_samples.length > UIScrollableContainerCSS.VELOCITY_WINDOW )
            this.velocity_samples.shift();
          let weighted = 0, total_w = 0;
          for ( let i = 0; i < this.velocity_samples.length; i++ )
          {
            const w = i + 1;
            weighted += this.velocity_samples[i] * w;
            total_w  += w;
          }
          this.scroll_velocity = weighted / total_w;
        }

        this.touch_last_pos  = p.clone();
        this.touch_last_time = now;
        return;
      }
    }

    super.onTouchMove( p, now );
  }

  public onTouchEnd( p: Vector2, now: number, p_global: Vector2 | null = null ): void
  {
    if ( this.native )
      return;

    const was_scrolling = this.is_scrolling;

    if ( this.touch_start_pos && this.touch_last_pos )
    {
      const deltaY = this.touch_last_pos.y - this.touch_start_pos.y;
      this.swipe_direction = Math.abs( deltaY ) > 10
        ? ( deltaY > 0 ? "down" : "up" )
        : null;
    }

    if ( this.snap_targets.length > 0 && !this.is_over_scrolling )
    {
      requestAnimationFrame( () => { this.snapToNearestTarget( this.swipe_direction ); } );
    }
    else
    {
      this.snapBackToBounds();
    }

    this.touch_start_pos = null;
    this.is_scrolling    = false;

    if ( !was_scrolling )
    {
      super.onTouchEnd( p, now, p_global );
      return;
    }

    const cancel_point = new Vector2( -1000000, -1000000 );
    super.onTouchEnd( cancel_point, now, cancel_point );
  }

  public update( now: number ): void
  {
    if ( this.last_update_time === null )
      this.last_update_time = now;

    const dt_ms           = Math.max( 0, now - this.last_update_time );
    this.last_update_time = now;

    this.updateBasic( now );

    if ( this.is_raf_animating ) return;
    if ( dt_ms < 1 ) return;

    if (
      this.options.enable_momentum &&
      !this.is_scrolling &&
      this.touch_start_pos === null &&
      Math.abs( this.scroll_velocity ) > 0.001
    )
    {
      const momentum_delta   = this.scroll_velocity * dt_ms * this.options.scroll_speed;
      const previous_offset  = this.scroll_offset;
      const adjusted_delta   = this.applyRubberBandEffect( momentum_delta );
      this.updateScrollOffset( this.scroll_offset + adjusted_delta );

      const hit_top    = this.scroll_offset <= 0 && this.scroll_velocity < 0;
      const hit_bottom = this.scroll_offset >= this.max_scroll_offset && this.scroll_velocity > 0;
      const no_move    = this.scroll_offset === previous_offset;

      if ( hit_top || hit_bottom || no_move )
      {
        this.scroll_velocity = 0;
        return;
      }

      const frame_ratio    = dt_ms / 16.6667;
      const friction       = Math.exp( this.log_friction * frame_ratio );
      this.scroll_velocity *= friction;

      if ( Math.abs( this.scroll_velocity ) < 0.02 )
        this.scroll_velocity = 0;
    }
  }

  protected override syncDomContent( _node: HTMLElement ): boolean
  {
    return true;
  }

  public render(): void
  {
    /* content lives on secondary CSS canvas — nothing drawn on main canvas,
       but the dirty flag must still be consumed or the parent re-renders forever */
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "UIScrollableContainerCSS";
  }
}
